import {
  Box,
  Link,
  List,
  ListItem,
  ListItemText,
  SxProps,
  Typography
} from '@mui/material';
import { Button } from 'system/atoms';
import { BackButton } from 'components/Buttons';
import { getSupportUrl } from 'core/utils/supportUrl';
import { FormikProps } from 'formik';
import LaunchIcon from '@mui/icons-material/Launch';
import { useFacebookLogin } from 'core/services/facebook/hooks/useFacebookLogin';
import {
  useGetOrganizationLazyQuery,
  useUpdateOrganizationPhoneNumberMutation
} from 'generated-graphql/hooks/apollo-hooks';
import { AnchorText } from 'components/Text';
import {
  whatsAppOnboardingStepMapper,
  WhatsAppOnboardingSteps
} from '../constants';
import { useDrawer } from 'services/drawer/hooks';
import { rest } from 'services/api';
import { useRecoilState, useRecoilValue } from 'recoil';
import { organizationAtom } from 'services/organization/atom';
import { Organization } from 'generated-graphql/types';
import { parsePhoneNumber } from 'libphonenumber-js';
import { userAtom } from 'services/user/atoms';
import { formatPhoneNumber } from 'utils/phoneNumbers';

type Props = {
  handleNext: (step: number) => void;
  handleBack: () => void;
  formik: FormikProps<{ phoneNumber: string }>;
};

const listItemStyle: SxProps = {
  display: 'list-item',
  p: 0
};

export const MetaLoginStep: React.FC<Props> = ({ formik, handleNext }) => {
  const [updatePhoneNumberMutation] =
    useUpdateOrganizationPhoneNumberMutation();
  const { closeDrawer } = useDrawer();

  const [organization, setOrganization] = useRecoilState(organizationAtom);
  const user = useRecoilValue(userAtom);

  const [fetchOrganization] = useGetOrganizationLazyQuery({
    onCompleted: ({ getOrganization }) => {
      setOrganization(getOrganization as Organization);
    },
    fetchPolicy: 'no-cache'
  });

  const onSuccess = async ({
    whatsAppBusinessAccountId
  }: {
    whatsAppBusinessAccountId: string;
  }) => {
    if (!organization) return;

    if (formik.values.phoneNumber) {
      await updatePhoneNumberMutation({
        variables: {
          input: {
            phoneNumber: parsePhoneNumber(formik.values.phoneNumber).number
          }
        }
      });
    }

    await rest.post('/twilio/register-whatsapp-sender', {
      whatsAppBusinessAccountId
    });

    await fetchOrganization({
      variables: {
        id: organization.id
      }
    });

    closeDrawer();
  };

  const { launchEmbeddedSignup } = useFacebookLogin({ onSuccess });
  const useSystemPhoneNumber = !formik.values.phoneNumber;

  const parsedPhoneNumber = formatPhoneNumber(formik.values.phoneNumber);

  const embeddedSignupParams = {
    useSystemPhoneNumber,
    websiteUrl: organization?.websiteUrl || undefined,
    phoneNumberWithoutCountryCode: parsedPhoneNumber?.nationalNumber,
    countryCode: parsedPhoneNumber?.countryCallingCode
      ? parseInt(parsedPhoneNumber?.countryCallingCode)
      : undefined,
    organizationName: organization?.name,
    email: user?.email
  };

  return (
    <Box>
      <BackButton
        onClick={() =>
          handleNext(
            whatsAppOnboardingStepMapper[WhatsAppOnboardingSteps.PHONE_NUMBER]
          )
        }
      />
      <Typography variant="h6" mt={3}>
        What next?
      </Typography>
      <List sx={{ listStyle: 'decimal' }} disablePadding>
        <ListItem>
          <ListItemText
            primary={
              <AnchorText
                onClick={() => launchEmbeddedSignup(embeddedSignupParams)}
              >
                <Typography>Log in to your Meta Business Account</Typography>
              </AnchorText>
            }
            sx={listItemStyle}
          />
        </ListItem>

        <ListItem>
          <ListItemText
            primary={
              <Typography>
                Follow the on-screen instructions to connect your phone number
                to your Meta Business Account
              </Typography>
            }
            sx={listItemStyle}
          />
        </ListItem>

        <ListItem>
          <ListItemText
            primary={
              <Typography>
                Once completed, you'll be redirected to Popp. You may have to
                wait a few minutes for the process to complete in the background{' '}
              </Typography>
            }
            sx={listItemStyle}
          />
        </ListItem>
      </List>

      <Typography mt={1}>
        If you need help,{' '}
        <Link
          href={getSupportUrl({ ticketForm: 'help_with_whatsapp_setup' })}
          target="_blank"
        >
          contact Popp's support team
        </Link>
      </Typography>

      <Box mt={4}>
        <Button
          endIcon={<LaunchIcon />}
          variant="primary"
          onClick={() => launchEmbeddedSignup(embeddedSignupParams)}
        >
          Log in to Meta
        </Button>
      </Box>
    </Box>
  );
};
